// Module 16 contracts (ADR-020): retention, purposes and legal basis, holds, subject requests,
// purges and the privacy action history. Messages are i18n keys (PD-015).
import { z } from 'zod';

const Uuid = z.uuid();
const trimmed = (max: number) => z.string().trim().min(1, 'v.required').max(max, 'v.tooLong');
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max, 'v.tooLong')
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
const Version = z.number().int().min(1);
const Reason = z.string().trim().min(3, 'v.reasonShort').max(1000, 'v.tooLong');
const ClassCode = z.string().regex(/^[a-z][a-z0-9_]{1,39}$/, 'v.invalid');
const Key = z.string().trim().regex(/^[a-z][a-z0-9_-]{0,39}$/, 'v.policyKey');
const LocalDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'v.date');

// ------------------------------------------------------------------ retention (PB-16-04)
export const RetentionRuleInput = z.object({
  durationDays: z.number().int('v.invalid').min(0, 'v.range').max(36_500, 'v.range').nullable(),
  basis: z.enum(['organisation', 'engineering_default']).default('organisation'),
  owner: optionalText(120),
  note: optionalText(300),
});
export const SaveRetentionDraftRequest = z.object({
  expectedVersion: z.number().int().min(0),
  rules: z.record(ClassCode, RetentionRuleInput),
  // PB-02-03 (PD-070): per-visitor-type durations; null removes that override.
  visitorTypes: z.record(Key, z.record(ClassCode, z.number().int('v.invalid').min(0, 'v.range').max(36_500, 'v.range').nullable())).optional(),
  note: optionalText(500),
});
export const PublishRetentionRequest = z.object({ expectedVersion: Version });
/** expectedVersion 0 = no draft yet. */
export const DraftVersionRequest = z.object({ expectedVersion: z.number().int().min(0) });

export interface RecordClassView {
  readonly code: string;
  readonly module: number;
  readonly trigger: string;
  readonly method: string;
  readonly stores: readonly string[];
  readonly subjectLinked: boolean;
  readonly executor: 'available' | 'no_store_yet';
  readonly engineeringDefaultDays: number | null;
}

export interface RetentionPolicyView {
  readonly id: string;
  readonly number: number;
  readonly status: 'draft' | 'published' | 'superseded' | 'discarded';
  readonly rules: Readonly<Record<string, { durationDays: number | null; basis: 'organisation' | 'engineering_default'; owner: string | null; note: string | null }>>;
  readonly note: string | null;
  readonly version: number;
  readonly publishedAt: string | null;
  readonly problems: ReadonlyArray<{ path: string; code: string }>;
  /** PB-02-03 (PD-070): per-visitor-type durations by type code, then class code. */
  readonly visitorTypes: Readonly<Record<string, Readonly<Record<string, number>>>>;
}

export interface RetentionOverview {
  readonly classes: readonly RecordClassView[];
  readonly published: RetentionPolicyView | null;
  readonly draft: RetentionPolicyView | null;
  readonly canManage: boolean;
}

// ------------------------------------------------------------------ privacy settings
/**
 * Partial update (PD-618): only the values sent change; a value marks it as set by the organisation,
 * `null` returns it to the engineering default. Omitted values keep what they are.
 */
export const PrivacySettingsInput = z.object({
  expectedVersion: z.number().int().min(0),
  requestResponseDays: z.number().int().min(1, 'v.range').max(365, 'v.range').nullable().optional(),
  requestExtensionDays: z.number().int().min(0, 'v.range').max(365, 'v.range').nullable().optional(),
  exportDownloadDays: z.number().int().min(1, 'v.range').max(90, 'v.range').nullable().optional(),
  anonymityThreshold: z.number().int().min(2, 'v.range').max(100, 'v.range').nullable().optional(),
  purgeFourEyes: z.boolean().nullable().optional(),
  /** PD-619: report/audit export download window in hours; null = the installation default. */
  reportExportHours: z.number().int().min(1, 'v.range').max(720, 'v.range').nullable().optional(),
});
export interface PrivacySettingsView {
  readonly requestResponseDays: number;
  readonly requestExtensionDays: number;
  readonly exportDownloadDays: number;
  readonly anonymityThreshold: number;
  readonly purgeFourEyes: boolean;
  readonly purgeAutoApply: false;
  /** PD-619: organisation window for report/audit export files (hours); null = installation default. */
  readonly reportExportHours: number | null;
  /** The installation's PASSAGE_EXPORT_TTL_HOURS (engineering default 24). */
  readonly installationReportExportHours: number;
  /** 0 = engineering defaults (nothing saved by the organisation yet). */
  readonly version: number;
  readonly source: 'engineering_default' | 'organisation';
  /** PD-618: per value, whether it is still the engineering default or set by the organisation. */
  readonly sources: Readonly<Record<string, 'engineering_default' | 'organisation'>>;
}

// ------------------------------------------------------------------ purposes and legal basis (PB-16-02)
export const LEGAL_BASIS_VALUES = ['consent', 'contract', 'legal_obligation', 'vital_interests', 'public_interest', 'legitimate_interests'] as const;
export const PurposeDraftRequest = z.object({
  code: Key,
  name: z.object({ en: trimmed(120), ar: z.string().trim().max(120, 'v.tooLong').optional() }),
  description: optionalText(1000),
  // PD-617: a code of the organisation's legal basis catalogue (placeholders or its own bases).
  basis: z.string().trim().regex(/^[a-z][a-z_]{1,39}$/, 'v.invalid'),
  dataClasses: z.array(ClassCode).min(1, 'v.required').max(40),
  noticeKey: Key.nullable().optional(),
  owner: optionalText(120),
});
export const PurposeApproveRequest = z.object({ expectedVersion: Version });
export interface PurposeView {
  readonly id: string;
  readonly code: string;
  readonly number: number;
  readonly name: { readonly en: string; readonly ar?: string };
  readonly description: string | null;
  readonly basis: string;
  /** PD-617: whether the basis needs a consent record (from the catalogue). */
  readonly requiresConsent: boolean;
  readonly dataClasses: readonly string[];
  readonly noticeKey: string | null;
  readonly owner: string | null;
  readonly status: 'draft' | 'approved' | 'retired';
  readonly approvedAt: string | null;
  readonly version: number;
  /** For consent purposes: acceptance counts per notice version and language (PB-16-01). */
  readonly consent: ReadonlyArray<{ readonly noticeNumber: number; readonly locale: string; readonly accepted: number; readonly declined: number }> | null;
  readonly withdrawals: number;
}

export const ConsentWithdrawalRequest = z.object({
  personId: Uuid,
  purposeCode: Key,
  channel: z.enum(['request', 'reception', 'email', 'in_person', 'other']),
  reason: optionalText(500),
});
export interface ConsentStatusView {
  readonly personId: string;
  readonly purposes: ReadonlyArray<{ readonly code: string; readonly basis: string; readonly allowed: boolean; readonly reason: string; readonly withdrawnAt: string | null }>;
}

// ------------------------------------------------------------------ holds (PB-16-07)
export const CreateHoldRequest = z.discriminatedUnion('scopeKind', [
  z.object({ scopeKind: z.literal('person'), personId: Uuid }),
  z.object({ scopeKind: z.literal('visit'), visitId: Uuid }),
  z.object({ scopeKind: z.literal('site'), siteId: Uuid, recordClass: ClassCode.nullable().optional() }),
  z.object({ scopeKind: z.literal('record_class'), recordClass: ClassCode }),
]).and(z.object({ reason: Reason, ownerUserId: Uuid, reviewOn: LocalDate }));
export const ReleaseHoldRequest = z.object({ expectedVersion: Version, reason: Reason });
export const ReviewHoldRequest = z.object({ expectedVersion: Version, note: Reason, reviewOn: LocalDate });
export interface HoldView {
  readonly id: string;
  readonly scopeKind: 'person' | 'visit' | 'site' | 'record_class';
  readonly personId: string | null;
  readonly visitId: string | null;
  readonly siteId: string | null;
  readonly recordClass: string | null;
  readonly reason: string;
  readonly owner: { readonly id: string; readonly name: string };
  readonly reviewOn: string;
  readonly reviewOverdue: boolean;
  readonly status: 'active' | 'released';
  readonly lastReviewedAt: string | null;
  readonly createdAt: string;
  readonly releasedAt: string | null;
  readonly releaseReason: string | null;
  readonly version: number;
  readonly canRelease: boolean;
}

// ------------------------------------------------------------------ subject requests (PB-16-08, PB-16-09)
export const SUBJECT_REQUEST_TYPE_VALUES = ['export', 'correction', 'erasure'] as const;
const Email = z.string().trim().max(254, 'v.tooLong').transform((v) => (v === '' ? null : v)).pipe(z.email('v.email').nullable()).nullable().optional();
const Phone = z.string().trim().transform((v) => (v === '' ? null : v)).pipe(z.string().regex(/^\+?[0-9 ()-]{6,24}$/, 'v.phone').nullable()).nullable().optional();
export const CORRECTABLE_FIELDS = ['givenName', 'familyName', 'email', 'phone'] as const;

export const CreateSubjectRequest = z.object({
  type: z.enum(SUBJECT_REQUEST_TYPE_VALUES),
  siteId: Uuid.nullable().optional(),
  requesterName: trimmed(200),
  requesterEmail: Email,
  requesterPhone: Phone,
  relationship: z.enum(['self', 'representative']).default('self'),
  receivedVia: z.enum(['email', 'web', 'in_person', 'post', 'phone', 'other']),
  receivedAt: z.iso.datetime({ offset: true }).optional(),
  corrections: z.object({ givenName: optionalText(100), familyName: optionalText(100), email: Email, phone: Phone }).partial().optional(),
});
export const VerifyRequesterRequest = z.object({
  expectedVersion: Version,
  method: z.enum(['document_sighted', 'authenticated_channel', 'known_contact_challenge', 'authorised_representative']),
  note: trimmed(500),
});
export const LinkSubjectRequest = z.object({ expectedVersion: Version, personId: Uuid });
export const ExtendRequest = z.object({ expectedVersion: Version, days: z.number().int().min(1).max(365), reason: Reason });
export const RequestCommand = z.object({ expectedVersion: Version, reason: optionalText(1000) });
export const ApplyCorrectionRequest = z.object({ expectedVersion: Version, personId: Uuid, personVersion: Version, fields: z.array(z.enum(CORRECTABLE_FIELDS)).min(1) });

export interface SubjectRequestSummary {
  readonly id: string;
  readonly reference: string;
  readonly type: (typeof SUBJECT_REQUEST_TYPE_VALUES)[number];
  readonly status: string;
  readonly siteId: string | null;
  /** Masked unless the caller may review requests (PB-16-10). */
  readonly requester: { readonly name: string | null; readonly email: string | null; readonly phone: string | null; readonly masked: boolean };
  readonly receivedAt: string;
  readonly dueAt: string;
  readonly overdue: boolean;
  readonly verified: boolean;
  readonly subjects: number;
  readonly hold: boolean;
  readonly version: number;
}

export interface SubjectCandidate {
  readonly personId: string;
  readonly displayName: string;
  readonly email: string | null;
  readonly phone: string | null;
  readonly matchBasis: readonly string[];
  readonly linkable: boolean;
  readonly refusal: 'contact_only' | 'no_match' | null;
  readonly visits: number;
}

export interface SubjectRequestDetail extends SubjectRequestSummary {
  readonly relationship: 'self' | 'representative';
  readonly receivedVia: string;
  readonly extensionDays: number;
  readonly extensionReason: string | null;
  readonly verification: { readonly method: string; readonly note: string | null; readonly at: string; readonly by: string } | null;
  readonly linked: ReadonlyArray<{ readonly personId: string; readonly displayName: string; readonly matchBasis: readonly string[]; readonly version: number; readonly current: Readonly<Record<string, string | null>> }>;
  readonly corrections: Readonly<Record<string, string | null>> | null;
  readonly holds: ReadonlyArray<{ readonly id: string; readonly scopeKind: string; readonly reason: string }>;
  readonly exports: ReadonlyArray<{ readonly id: string; readonly status: string; readonly preparedAt: string; readonly releasedAt: string | null; readonly expiresAt: string | null; readonly summary: Readonly<Record<string, unknown>>; readonly downloadCount: number }>;
  readonly purgeRunId: string | null;
  readonly outcomeNote: string | null;
  readonly closedAt: string | null;
  readonly allowed: readonly string[];
}

// ------------------------------------------------------------------ purges (PB-16-05, PB-16-06, PB-16-09)
export const PlanPurgeRequest = z.object({
  kind: z.enum(['retention', 'anonymisation']),
  dryRun: z.boolean().default(false),
  classes: z.array(ClassCode).max(40).optional(),
});
export const ApplyPurgeRequest = z.object({ expectedVersion: Version, acknowledgeIrreversible: z.literal(true, 'v.acknowledgeRequired') });
export const CancelPurgeRequest = z.object({ expectedVersion: Version, reason: Reason });
export const RetryPurgeRequest = z.object({ expectedVersion: Version });

export interface PurgeRunView {
  readonly id: string;
  /** 'minimisation': reviewed purge of values hidden by the collection matrix (PB-16-03, PD-613). */
  readonly kind: 'retention' | 'anonymisation' | 'erasure' | 'minimisation';
  readonly requestId: string | null;
  readonly dryRun: boolean;
  readonly status: 'planned' | 'applying' | 'completed' | 'partial' | 'cancelled';
  readonly summary: {
    readonly byClass?: Readonly<Record<string, Readonly<Record<string, number>>>>;
    readonly retainedAggregates?: ReadonlyArray<{ readonly key: string; readonly count: number }>;
    readonly riskyGroups?: number;
    readonly policyNumber?: number | null;
    readonly engineeringDefaults?: readonly string[];
    /** PB-12-05: site retention overrides in force when the run was planned. */
    readonly siteOverrides?: ReadonlyArray<{ readonly siteId: string; readonly number: number }>;
    /** PB-16-03: held values per collection-matrix item in a minimisation run. */
    readonly minimisation?: { readonly byItem: Readonly<Record<string, number>> };
  };
  readonly plannedBy: string | null;
  readonly plannedAt: string;
  readonly appliedBy: string | null;
  readonly appliedAt: string | null;
  readonly completedAt: string | null;
  readonly retryOwner: string | null;
  readonly version: number;
  readonly counts: Readonly<Record<string, number>>;
  readonly allowed: readonly ('apply' | 'cancel' | 'retry')[];
}

export interface PurgeItemView {
  readonly id: string;
  readonly recordClass: string;
  readonly store: 'database' | 'object_store';
  readonly resourceType: string;
  /** Safe identifier only (never the value being erased). */
  readonly resourceId: string;
  readonly method: string;
  readonly status: string;
  readonly exceptionCode: string | null;
  readonly holdIds: readonly string[];
  readonly attempts: number;
  readonly lastError: string | null;
  /** PB-16-03: for a held value, the collection-matrix item (field) and the record it belongs to. */
  readonly field?: string;
  readonly recordId?: string;
}

export interface PrivacyHistoryItem {
  readonly id: string;
  readonly occurredAt: string;
  readonly actorUserId: string | null;
  readonly actorType: string;
  readonly action: string;
  readonly resourceType: string;
  readonly resourceId: string | null;
  readonly outcome: string;
  readonly reason: string | null;
}

export interface SensitiveFieldGrantView {
  readonly fieldClass: string;
  readonly read: readonly string[];
  readonly reveal: readonly string[];
  readonly export: readonly string[];
  readonly yours: { readonly read: boolean; readonly reveal: boolean; readonly export: boolean };
}
